import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "../components/Loader";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_SERIES,
} from "../shared/chartTheme";

import "./ReportPage.css";

import {
  fetchCashierPerformanceReport,
  fetchOpenShiftsReport,
  fetchSalesDashboard,
  fetchSalesTrendsReport,
  fetchShiftsReport,
  selectCashierPerformanceData,
  selectOpenShiftsData,
  selectSalesDashboardData,
  selectSalesDashboardError,
  selectSalesDashboardStatus,
  selectSalesTrendsData,
  selectShiftsData,
} from "../features/reports/reportsSlice";
import { fetchStores, selectStores, selectStoresStatus } from "../features/stores/storesSlice";
import StoreMultiSelect from "../components/StoreMultiSelect";

/**
 * Страница «Продажи» — замена дашборда Metabase «AUF Продажа» (AUFB-65).
 *
 * Раньше здесь был iframe с Metabase. Теперь данные берутся из наших отчётных
 * эндпоинтов: одна сводка на всю страницу (`/api/reports/sales-dashboard/`) плюс
 * четыре отдельных отчёта — открытые смены, кассиры, смены и динамика продаж.
 */

const PAYMENT_TYPE_LABELS = {
  CASH: "Наличные",
  CARD: "Карта",
  QR: "QR",
  ONLINE: "Онлайн",
  NONE: "Без оплаты",
};

// Палитра берётся из тех же CSS-переменных, что и остальная тема, чтобы графики
// не выбивались из светлой/тёмной схемы.
const CHART_COLORS = CHART_SERIES;

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCount(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU");
}

function formatPct(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00%";
  return `${num.toFixed(2)}%`;
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function formatAxisDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatTooltipDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

function todayIso() {
  return toIsoLocal(new Date());
}

/** Сдвиг в процентах текущего периода к предыдущему; null, если сравнивать не с чем. */
function deltaPct(current, previous) {
  const cur = Number(current || 0);
  const prev = Number(previous || 0);
  if (!prev) return null;
  return ((cur - prev) / Math.abs(prev)) * 100;
}

function DeltaHint({ current, previous, suffix = "к прошлому периоду" }) {
  const delta = deltaPct(current, previous);
  if (delta === null) return null;
  const positive = delta >= 0;
  return (
    <span className={positive ? "delta-up" : "delta-down"}>
      {positive ? "▲" : "▼"} {formatPct(Math.abs(delta))} {suffix}
    </span>
  );
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div className="kpi-card">
      <div className="kpi-title">{title}</div>
      <div className={accent ? `kpi-value ${accent}` : "kpi-value"}>{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="section">
      <div className="section-head">
        <div className="section-title">{title}</div>
        {subtitle && <div className="section-subtitle">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Empty({ text = "Нет данных за выбранный период" }) {
  return <div className="empty">{text}</div>;
}

/** Универсальная таблица: колонки описываются дескрипторами, как на странице P&L. */
function BreakdownTable({ rows, columns, emptyText }) {
  if (!rows || rows.length === 0) return <Empty text={emptyText} />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.numeric || col.money || col.pct ? "numeric" : undefined}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id ?? index}>
              {columns.map((col) => {
                const raw = row[col.key];
                let content = raw;
                if (col.money) content = formatMoney(raw);
                else if (col.pct) content = formatPct(raw);
                else if (col.numeric) content = formatCount(raw);
                else if (raw === null || raw === undefined || raw === "") content = "—";
                const cellClasses = [
                  col.numeric || col.money || col.pct ? "numeric" : "",
                  col.bold ? "bold" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <td key={col.key} className={cellClasses || undefined}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const tooltipStyle = {
  background: "var(--c-card)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  color: "var(--c-text)",
  fontSize: 14,
};

export default function DashboardPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const data = useSelector(selectSalesDashboardData);
  const status = useSelector(selectSalesDashboardStatus);
  const error = useSelector(selectSalesDashboardError);
  const openShifts = useSelector(selectOpenShiftsData);
  const cashiers = useSelector(selectCashierPerformanceData);
  const shifts = useSelector(selectShiftsData);
  const trends = useSelector(selectSalesTrendsData);

  const [storeIds, setStoreIds] = useState(() => {
    const raw = searchParams.getAll("store_ids");
    return raw.length ? raw.map(String) : [];
  });
  const [startDate, setStartDate] = useState(() => searchParams.get("start_date") || todayIso());
  const [endDate, setEndDate] = useState(() => searchParams.get("end_date") || todayIso());

  useEffect(() => {
    if (storesStatus === "idle") dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch, storesStatus]);

  const load = useCallback(() => {
    const numericIds = storeIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    const common = { storeIds: numericIds, startDate, endDate };
    dispatch(fetchSalesDashboard({ ...common, topProducts: 10 }));
    dispatch(fetchOpenShiftsReport({ storeIds: numericIds }));
    dispatch(fetchCashierPerformanceReport({ ...common, sortBy: "sales", limit: 10 }));
    dispatch(fetchShiftsReport({ ...common, limit: 50 }));
    dispatch(fetchSalesTrendsReport({ ...common, topN: 5 }));
  }, [dispatch, storeIds, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  // Фильтры держим в URL — так ссылку на конкретный период можно переслать.
  useEffect(() => {
    const params = new URLSearchParams();
    storeIds.forEach((id) => params.append("store_ids", String(id)));
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    setSearchParams(params, { replace: true });
  }, [storeIds, startDate, endDate, setSearchParams]);

  const handleReset = () => {
    setStoreIds([]);
    setStartDate(todayIso());
    setEndDate(todayIso());
  };

  const totals = data?.totals;
  const previous = data?.previous_period;

  const dailyRows = useMemo(
    () =>
      (data?.daily || []).map((row) => ({
        ...row,
        net_revenue: Number(row.net_revenue || 0),
        avg_check: Number(row.avg_check || 0),
      })),
    [data]
  );

  const storeRows = useMemo(
    () =>
      (data?.by_store || []).map((row) => ({
        ...row,
        id: row.store_id,
        net_revenue_num: Number(row.net_revenue || 0),
      })),
    [data]
  );

  const paymentRows = useMemo(
    () =>
      (data?.by_payment_type || []).map((row) => ({
        ...row,
        id: row.payment_type,
        label: PAYMENT_TYPE_LABELS[row.payment_type] || row.payment_type || "—",
        amount_num: Math.abs(Number(row.amount || 0)),
      })),
    [data]
  );

  const refundRows = useMemo(
    () => (data?.by_refund_reason || []).map((row) => ({ ...row, id: row.reason })),
    [data]
  );

  const productRows = useMemo(
    () => (data?.top_products?.items || []).map((row) => ({ ...row, id: row.product_id })),
    [data]
  );

  const cashierRows = useMemo(
    () =>
      (cashiers?.cashiers || []).map((row, i) => ({
        ...row,
        id: row.cashier_id ?? i,
        total_sales_num: Number(row.total_sales || 0),
      })),
    [cashiers]
  );

  const weekdayRows = useMemo(
    () =>
      (trends?.by_weekday || []).map((row) => ({
        ...row,
        // `weekday_name` приходит с сервера (ISO: 1 = Понедельник) — своей таблицы
        // дней недели на фронте нет намеренно, иначе нумерация разъедется.
        // На оси семь столбцов, поэтому «Понедельник» сокращаем до «Пон».
        label: (row.weekday_name || String(row.weekday)).slice(0, 3),
        revenue_num: Number(row.revenue || 0),
      })),
    [trends]
  );

  const openShiftRows = useMemo(
    () => (openShifts?.shifts || []).map((row, i) => ({ ...row, id: row.shift_id ?? i })),
    [openShifts]
  );

  const shiftRows = useMemo(
    () => (shifts?.by_shift || []).map((row, i) => ({ ...row, id: row.shift_id ?? i })),
    [shifts]
  );

  const isLoading = status === "loading";

  return (
    <div className="sales-dashboard-page">
      <div className="header">
        <div>
          <div className="title">Продажи</div>
          <div className="subtitle">
            Выручка, чеки и возвраты по магазинам за выбранный период
          </div>
        </div>
      </div>

      <div className="toolbar">
        <StoreMultiSelect stores={stores} value={storeIds} onChange={setStoreIds} />
        <div className="field">
          <label className="label">С даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div className="field">
          <label className="label">По дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div className="toolbar-buttons">
          <button type="button" onClick={load} className="apply-btn">
            Обновить
          </button>
          <button type="button" onClick={handleReset} className="reset-btn">
            Сбросить
          </button>
        </div>
      </div>

      {isLoading && <Skeleton variant="cards" count={4} text="Загрузка отчёта…" style={{ padding: "16px 24px 32px" }} />}
      {status === "failed" && error && <div className="error-box">{error}</div>}

      <div className="body">
        <div className="kpi-grid">
          <KpiCard
            title="Выручка"
            value={formatMoney(totals?.net_revenue)}
            accent="accent"
            hint={<DeltaHint current={totals?.net_revenue} previous={previous?.net_revenue} />}
          />
          <KpiCard
            title="Чеки"
            value={formatCount(totals?.sales_count)}
            hint={<DeltaHint current={totals?.sales_count} previous={previous?.sales_count} />}
          />
          <KpiCard
            title="Средний чек"
            value={formatMoney(totals?.avg_check)}
            hint={<DeltaHint current={totals?.avg_check} previous={previous?.avg_check} />}
          />
          <KpiCard
            title="Возвраты"
            value={formatMoney(totals?.refunds_amount)}
            accent="danger"
            hint={`${formatCount(totals?.refunds_count)} шт.`}
          />
          <KpiCard title="Скидки" value={formatMoney(totals?.discount_total)} />
          <KpiCard title="Бонусы" value={formatMoney(totals?.bonus_used)} />
        </div>

        <Section title="Выручка по дням">
          {dailyRows.length === 0 ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={dailyRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis
                  dataKey="day"
                  tickFormatter={formatAxisDate}
                  stroke="var(--c-text-muted)"
                  fontSize={12}
                />
                <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={formatTooltipDate}
                  formatter={(value, name) => [formatMoney(value), name]}
                />
                <Legend wrapperStyle={{ fontSize: 14 }} />
                <Line
                  type="monotone"
                  dataKey="net_revenue"
                  name="Выручка"
                  stroke={CHART_COLORS[0]}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="avg_check"
                  name="Средний чек"
                  stroke={CHART_COLORS[1]}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Section>

        <div className="two-col">
          <Section title="Продажи по магазинам">
            {storeRows.length === 0 ? (
              <Empty />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={storeRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                    <XAxis dataKey="store" stroke="var(--c-text-muted)" fontSize={11} />
                    <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
                    <Bar dataKey="net_revenue_num" name="Выручка" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <BreakdownTable
                  rows={storeRows}
                  columns={[
                    { key: "store", title: "Магазин" },
                    { key: "net_revenue", title: "Выручка", money: true, bold: true },
                    { key: "sales_count", title: "Чеки", numeric: true },
                    { key: "refunds_count", title: "Возвраты", numeric: true },
                  ]}
                />
              </>
            )}
          </Section>

          <Section title="Способы оплаты">
            {paymentRows.length === 0 ? (
              <Empty />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={paymentRows}
                      dataKey="amount_num"
                      nameKey="label"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                    >
                      {paymentRows.map((row, index) => (
                        <Cell key={row.id} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
                    <Legend wrapperStyle={{ fontSize: 14 }} />
                  </PieChart>
                </ResponsiveContainer>
                <BreakdownTable
                  rows={paymentRows}
                  columns={[
                    { key: "label", title: "Способ" },
                    { key: "amount", title: "Сумма", money: true, bold: true },
                    { key: "count", title: "Чеки", numeric: true },
                  ]}
                />
              </>
            )}
          </Section>
        </div>

        <Section title="Топ товаров" subtitle="По сумме продаж за период">
          <BreakdownTable
            rows={productRows}
            columns={[
              { key: "product_name", title: "Товар" },
              { key: "quantity", title: "Кол-во", numeric: true },
              { key: "amount", title: "Сумма", money: true, bold: true },
            ]}
            emptyText="За период не продано ни одного товара"
          />
          {data?.top_products?.other && Number(data.top_products.other.amount || 0) !== 0 && (
            <div className="hint-block">
              Остальные товары: {formatMoney(data.top_products.other.amount)} (
              {formatCount(data.top_products.other.quantity)} шт.)
            </div>
          )}
        </Section>

        <div className="two-col">
          <Section title="Причины возвратов">
            <BreakdownTable
              rows={refundRows}
              columns={[
                { key: "reason_label", title: "Причина" },
                { key: "amount", title: "Сумма", money: true, bold: true },
                { key: "count", title: "Штук", numeric: true },
              ]}
              emptyText="Возвратов за период не было"
            />
          </Section>

          <Section title="Открытые смены" subtitle="Состояние касс прямо сейчас">
            <BreakdownTable
              rows={openShiftRows}
              columns={[
                { key: "store", title: "Магазин" },
                { key: "cashier", title: "Кассир" },
                { key: "total_sales", title: "Продажи", money: true, bold: true },
                { key: "sales_count", title: "Чеки", numeric: true },
                { key: "avg_check", title: "Средний чек", money: true },
              ]}
              emptyText="Открытых смен нет"
            />
          </Section>
        </div>

        <Section title="Продажи по кассирам">
          {cashierRows.length === 0 ? (
            <Empty />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={cashierRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                  <XAxis dataKey="cashier_name" stroke="var(--c-text-muted)" fontSize={11} />
                  <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
                  <Bar dataKey="total_sales_num" name="Продажи" fill={CHART_COLORS[2]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <BreakdownTable
                rows={cashierRows}
                columns={[
                  { key: "cashier_name", title: "Кассир" },
                  { key: "total_sales", title: "Продажи", money: true, bold: true },
                  { key: "gross_profit", title: "Прибыль", money: true },
                  { key: "transaction_count", title: "Чеки", numeric: true },
                  { key: "shift_count", title: "Смены", numeric: true },
                ]}
              />
            </>
          )}
        </Section>

        <div className="two-col">
          <Section title="Продажи по дням недели">
            {weekdayRows.length === 0 ? (
              <Empty />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={weekdayRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                  <XAxis dataKey="label" stroke="var(--c-text-muted)" fontSize={12} />
                  <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatMoney(v)} />
                  <Bar dataKey="revenue_num" name="Выручка" fill={CHART_COLORS[4]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Section>

          <Section title="Лучшие дни">
            <BreakdownTable
              rows={(trends?.top_days || []).map((row, i) => ({ ...row, id: row.date ?? i }))}
              columns={[
                { key: "date", title: "День" },
                { key: "weekday_name", title: "День недели" },
                { key: "revenue", title: "Выручка", money: true, bold: true },
                { key: "vs_weekday_avg_pct", title: "К среднему", pct: true },
              ]}
            />
          </Section>
        </div>

        <Section title="Смены за период">
          <BreakdownTable
            rows={shiftRows}
            columns={[
              { key: "day", title: "День" },
              { key: "store", title: "Магазин" },
              { key: "cashier_name", title: "Кассир" },
              { key: "total_sales", title: "Продажи", money: true, bold: true },
              { key: "cash_difference", title: "Недостача", money: true },
              { key: "transaction_count", title: "Чеки", numeric: true },
            ]}
            emptyText="Смен за период не было"
          />
        </Section>
      </div>
    </div>
  );
}
